'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { formatCents } from '@/lib/utils/formatAmount';
import { BUTTON_M_CLASS, LABEL_CLASS, TOOLTIP_CONTENT_CLASS, Z_OVERLAY_MAX } from '../constants';

interface TargetPriceFieldProps {
  valueCents: number;
  onChangeCents: (cents: number) => void;
  currentCents: number;
  isLoading?: boolean;
}

const MIN = 1;
const MAX = 99;

export function TargetPriceField({
  valueCents,
  onChangeCents,
  currentCents,
  isLoading = false,
}: TargetPriceFieldProps) {
  const tf = useTranslations('priceAlerts.form');
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const display = focused ? draft : String(valueCents);

  const clamp = (n: number) => Math.min(MAX, Math.max(MIN, n));

  const step = (delta: number) => {
    const next = clamp(valueCents + delta);
    onChangeCents(next);
    setDraft(String(next));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Digits only, no leading zeros ("05" -> "5")
    const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    setDraft(digits);
    if (digits === '') return;
    onChangeCents(clamp(Number.parseInt(digits, 10)));
  };

  const handleFocus = () => {
    setDraft(String(valueCents));
    setFocused(true);
  };
  const handleBlur = () => {
    setFocused(false);
    if (draft.replace(/\D/g, '') === '') onChangeCents(0);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className={LABEL_CLASS}>{tf('targetPrice')}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={tf('currentTooltip.price')}
              className="group flex cursor-pointer items-center gap-0.5 text-[13px] leading-4 font-normal outline-none"
            >
              <span className="text-v2-text-primary opacity-50">{tf('current')}</span>
              {isLoading ? (
                <Loader2 className="ml-1 size-3.5 animate-spin text-v2-text-secondary" />
              ) : (
                <span className="text-v2-text-primary">&nbsp;{formatCents(currentCents)}</span>
              )}
              <Icon
                name="Attention"
                size={12}
                className="ml-1 text-v2-text-secondary transition-colors group-hover:text-v2-text-primary"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            align="end"
            showArrow={false}
            style={{ textWrap: 'wrap', zIndex: Z_OVERLAY_MAX }}
            className={TOOLTIP_CONTENT_CLASS}
          >
            {tf('currentTooltip.price')}
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex h-11 items-center justify-between rounded-[10px] bg-v2-btn-secondary px-4">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="size-5 animate-spin text-v2-text-secondary" />
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Decrease target"
              disabled={valueCents <= MIN}
              className="cursor-pointer text-v2-text-primary transition-opacity hover:opacity-70 disabled:opacity-30"
            >
              <Icon name="Minus" size={24} />
            </button>
            <div className="flex min-w-0 flex-1 items-center justify-center">
              <input
                type="text"
                inputMode="numeric"
                value={display}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                aria-label={tf('targetPrice')}
                size={Math.max(display.length, 1)}
                className={cn(
                  'min-w-0 bg-transparent text-right text-v2-text-primary outline-none',
                  BUTTON_M_CLASS,
                )}
              />
              <span className={cn('text-v2-text-primary', BUTTON_M_CLASS)}>¢</span>
            </div>
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Increase target"
              disabled={valueCents >= MAX}
              className="cursor-pointer text-v2-text-primary transition-opacity hover:opacity-70 disabled:opacity-30"
            >
              <Icon name="Plus" size={24} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
